import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/utils/cn";
import { uiConfig } from "@/config";
import { Button } from "./button";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?:
    | "default"
    | "success"
    | "error"
    | "warning"
    | "info"
    | "destructive";
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => string;
  removeToast: (id: string) => void;
  removeAll: () => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(
  undefined,
);

export function useEnhancedToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error(
      "useEnhancedToast must be used within a EnhancedToastProvider",
    );
  }
  return context;
}

export function EnhancedToastProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timeoutRefs = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  const addToast = React.useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { ...toast, id };
    setToasts((prev) => [...prev, newToast]);

    // Auto remove after duration (unless it has an action)
    if (!toast.action) {
      const duration = toast.duration || uiConfig.toastDuration || 5000;
      const timeout = setTimeout(() => {
        removeToast(id);
      }, duration);
      timeoutRefs.current.set(id, timeout);
    }

    return id;
  }, []);

  const removeToast = React.useCallback((id: string) => {
    // Clear timeout if exists
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }

    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const removeAll = React.useCallback(() => {
    // Clear all timeouts
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current.clear();

    setToasts([]);
  }, []);

  // Clean up timeouts on unmount
  React.useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  // Listen for global toast events
  React.useEffect(() => {
    const handleToastEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        // Map destructive variant to error
        const variant =
          customEvent.detail.variant === "destructive"
            ? "error"
            : customEvent.detail.variant;
        addToast({ ...customEvent.detail, variant });
      }
    };

    window.addEventListener("app:toast", handleToastEvent);
    return () => {
      window.removeEventListener("app:toast", handleToastEvent);
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, removeAll }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

function ToastContainer() {
  const { toasts, removeToast } = useEnhancedToast();

  return (
    <div
      data-toast-container
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none max-h-screen overflow-hidden"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
}

const ToastItem = React.forwardRef<
  HTMLDivElement,
  { toast: Toast; onRemove: (id: string) => void }
>(({ toast, onRemove }, ref) => {
  const getIcon = () => {
    switch (toast.variant) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "error":
      case "destructive":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "info":
        return <Info className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getVariantStyles = () => {
    switch (toast.variant) {
      case "success":
        return "border-green-500/30 bg-green-500/10";
      case "error":
      case "destructive":
        return "border-red-500/30 bg-red-500/10";
      case "warning":
        return "border-yellow-500/30 bg-yellow-500/10";
      case "info":
        return "border-blue-500/30 bg-blue-500/10";
      default:
        return "border-border bg-background/95";
    }
  };

  const handleAction = () => {
    if (toast.action) {
      toast.action.onClick();
      onRemove(toast.id);
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className={cn(
        "pointer-events-auto min-w-[300px] max-w-md rounded-lg border p-4",
        "backdrop-blur-xl shadow-2xl",
        getVariantStyles(),
      )}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <h3 className="font-semibold text-sm">{toast.title}</h3>
          {toast.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {toast.description}
            </p>
          )}
          {toast.action && (
            <div className="mt-3">
              <Button
                size="sm"
                variant={
                  toast.variant === "destructive" || toast.variant === "error"
                    ? "destructive"
                    : "default"
                }
                onClick={handleAction}
              >
                {toast.action.label}
              </Button>
            </div>
          )}
        </div>
        <button
          onClick={() => onRemove(toast.id)}
          className="text-muted-foreground hover:text-foreground transition-colors -mt-1 -mr-1 p-1 rounded-md hover:bg-muted/20"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
});

// Hook for easier usage
export function useToast() {
  const { addToast, removeToast, removeAll } = useEnhancedToast();

  const toast = React.useCallback(
    (options: Omit<Toast, "id">) => {
      return addToast(options);
    },
    [addToast],
  );

  const success = React.useCallback(
    (title: string, description?: string) => {
      return addToast({ title, description: description ?? "", variant: "success" });
    },
    [addToast],
  );

  const error = React.useCallback(
    (title: string, description?: string) => {
      return addToast({ title, description: description ?? "", variant: "error" });
    },
    [addToast],
  );

  const warning = React.useCallback(
    (title: string, description?: string) => {
      return addToast({ title, description: description ?? "", variant: "warning" });
    },
    [addToast],
  );

  const info = React.useCallback(
    (title: string, description?: string) => {
      return addToast({ title, description: description ?? "", variant: "info" });
    },
    [addToast],
  );

  return {
    toast,
    success,
    error,
    warning,
    info,
    dismiss: removeToast,
    dismissAll: removeAll,
  };
}
